/**
 * Recently-viewed geometries and studies that the signed-in user does NOT own
 * (ENG-2009) — the only record anywhere that someone opened a share link.
 *
 * The backend keeps no view history and exposes no owner on the list payloads,
 * so this is written client-side from what the detail route already had in
 * hand. Every function here touches `localStorage` synchronously.
 *
 * The key is scoped per user AND per kind
 * (`khorium_recently_viewed:<userId>:<kind>`, `:anon:` signed out) rather than
 * cleared at logout the way `chatPrefs` is: this list
 * must survive a logout and come back on re-login, which clear-on-logout
 * cannot do, while still keeping one user's history off a shared browser's
 * next user. The suffix also puts it outside `clearPersistedState`'s key list,
 * so the workspace "start fresh" leaves it alone — correct, since this is
 * cross-workspace history, not project state.
 *
 * Reads are validated, not trusted: the blob is user-writable and outlives app
 * versions. A wrong version is discarded rather than migrated, and a single
 * malformed entry is dropped without costing the user the rest of the list.
 */
import { safeParseJSON, STORAGE_KEYS } from '@/utils/sessionStorage';

export type RecentViewKind = 'geometry' | 'study';

export interface RecentView {
  id: string;
  kind: RecentViewKind;
  /** Name as it read when viewed — may be stale, by design. */
  name: string;
  /**
   * Owner, captured opportunistically. `owner_email` lives only on the
   * owner-only `/sharing` endpoint, so a viewer of a shared link cannot fetch
   * it and this is normally absent. Stored anyway so the column fills in for
   * free if the backend ever puts an owner on the list payload.
   */
  ownerLabel?: string;
  /**
   * The resource's status when viewed, for a study. Only used to pick the
   * landing route (a completed study opens on `/results`, matching the SimOps
   * dashboard), so a stale value costs a suboptimal landing, not correctness.
   */
  status?: string;
  /** ISO timestamp of the most recent view. */
  viewedAt: string;
}

/** Shape actually written to disk, so the schema can be versioned. */
interface StoredRecentViews {
  readonly version: 1;
  readonly entries: readonly unknown[];
}

const VERSION = 1;

/** Most recent N per kind. Older views past this are dropped on write. */
export const RECENT_VIEW_LIMIT = 100;

// Kind is part of the key, not a field filtered out of one shared blob. The cap
// is per kind anyway, and a shared blob made every geometry view re-serialize
// the untouched study list (and vice versa) for nothing.
function storageKey(userId: string | null, kind: RecentViewKind): string {
  return `${STORAGE_KEYS.RECENTLY_VIEWED}:${userId ?? 'anon'}:${kind}`;
}

function isRecentView(value: unknown): value is RecentView {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Partial<RecentView>;
  return (
    typeof entry.id === 'string' &&
    entry.id.length > 0 &&
    (entry.kind === 'geometry' || entry.kind === 'study') &&
    typeof entry.name === 'string' &&
    typeof entry.viewedAt === 'string' &&
    (entry.ownerLabel === undefined || typeof entry.ownerLabel === 'string') &&
    (entry.status === undefined || typeof entry.status === 'string')
  );
}

function readAll(userId: string | null, kind: RecentViewKind): RecentView[] {
  if (typeof window === 'undefined') return [];
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(storageKey(userId, kind));
  } catch {
    // Private-mode / blocked storage. An empty list is a correct answer.
    return [];
  }
  if (!raw) return [];

  const parsed = safeParseJSON<unknown>(raw, null);
  if (typeof parsed !== 'object' || parsed === null) return [];
  const stored = parsed as Partial<StoredRecentViews>;
  // An unknown version is a future schema this build cannot read. Showing
  // nothing beats guessing at fields that may have changed meaning.
  if (stored.version !== VERSION) return [];
  if (!Array.isArray(stored.entries)) return [];

  // A row under the wrong kind's key is corrupt, not a row of the other kind.
  return stored.entries.filter(
    (e): e is RecentView => isRecentView(e) && e.kind === kind,
  );
}

function writeAll(
  userId: string | null,
  kind: RecentViewKind,
  entries: readonly RecentView[],
): void {
  if (typeof window === 'undefined') return;
  const payload: StoredRecentViews = { version: VERSION, entries };
  try {
    window.localStorage.setItem(
      storageKey(userId, kind),
      JSON.stringify(payload),
    );
  } catch {
    // Best-effort only — a failed write costs the history, not the session.
  }
}

/** One product's list, newest first. */
export function loadRecentViews(
  userId: string | null,
  kind: RecentViewKind,
): RecentView[] {
  return readAll(userId, kind).sort((a, b) =>
    b.viewedAt.localeCompare(a.viewedAt),
  );
}

/**
 * Record a view, de-duped on id: re-viewing bumps `viewedAt` and refreshes the
 * cached name/status/owner rather than appending a second row. Kinds are
 * capped independently (separate keys), so a busy MeshGen history can't evict
 * studies.
 */
export function recordRecentView(
  userId: string | null,
  entry: RecentView,
): void {
  const kept = readAll(userId, entry.kind).filter((e) => e.id !== entry.id);
  const next = [entry, ...kept]
    .sort((a, b) => b.viewedAt.localeCompare(a.viewedAt))
    .slice(0, RECENT_VIEW_LIMIT);
  writeAll(userId, entry.kind, next);
}

/** Drop one row — the "Remove from Shared with me" action. */
export function removeRecentView(
  userId: string | null,
  kind: RecentViewKind,
  id: string,
): void {
  const entries = readAll(userId, kind);
  const kept = entries.filter((e) => e.id !== id);
  if (kept.length !== entries.length) writeAll(userId, kind, kept);
}
